// ============================================================
// pages/admin/AdminProducts.jsx — Product Management (Firebase)
// ============================================================
// Full CRUD with flexible pricing models, image upload to Storage

import React, { useState, useRef } from "react";
import { useProducts } from "../../hooks/useFirestore";
import { ProductService } from "../../services/firebaseServices";

// PriceDisplay helper
const PriceDisplay = ({ product }) => {
  const disc = product.discountPercent;
  if (product.pricingType === "per_size" && product.sizes?.length) {
    const min = Math.min(...product.sizes.map((s) => s.promoPrice || s.price));
    const max = Math.max(...product.sizes.map((s) => s.promoPrice || s.price));
    const origMin = Math.min(...product.sizes.map((s) => s.price));
    const origMax = Math.max(...product.sizes.map((s) => s.price));
    const hasPromo = product.sizes.some((s) => s.promoPrice);
    if (hasPromo || disc) {
      const dMin = disc ? Math.round(origMin * (1 - disc / 100)) : min;
      const dMax = disc ? Math.round(origMax * (1 - disc / 100)) : max;
      return (
        <span>
          <span style={{ textDecoration: "line-through", opacity: 0.5 }}>Rs {origMin} — Rs {origMax}</span>{" "}
          <span style={{ color: "#e74c3c" }}>Rs {dMin} — Rs {dMax}</span>
        </span>
      );
    }
    return <span>Rs {min} — Rs {max}</span>;
  }
  if (product.pricingType === "range") {
    const hasPromo = product.promoRangeMin || disc;
    if (hasPromo) {
      const dMin = disc ? Math.round(product.rangeMin * (1 - disc / 100)) : product.promoRangeMin;
      const dMax = disc ? Math.round(product.rangeMax * (1 - disc / 100)) : product.promoRangeMax;
      return (
        <span>
          <span style={{ textDecoration: "line-through", opacity: 0.5 }}>Rs {product.rangeMin} — Rs {product.rangeMax}</span>{" "}
          <span style={{ color: "#e74c3c" }}>Rs {dMin} — Rs {dMax}</span>
        </span>
      );
    }
    return <span>Rs {product.rangeMin} — Rs {product.rangeMax}</span>;
  }
  if (product.pricingType === "fixed") {
    const finalPrice = product.promoPrice || (disc ? Math.round(product.fixedPrice * (1 - disc / 100)) : null);
    if (finalPrice) {
      return (
        <span>
          <span style={{ textDecoration: "line-through", opacity: 0.5 }}>Rs {product.fixedPrice}</span>{" "}
          <span style={{ color: "#e74c3c" }}>Rs {finalPrice}</span>
        </span>
      );
    }
    return <span>Rs {product.fixedPrice}</span>;
  }
  return null;
};

// ============================================================
// PRODUCT EDITOR COMPONENT
// ============================================================
function ProductEditor({ product, onSave, onCancel }) {
  const isNew = !product?.id;
  const [form, setForm] = useState(product || {
    name: "", description: "", category: "cakes",
    pricingType: "fixed", fixedPrice: 0, promoPrice: null,
    sizes: [{ label: "", price: 0, promoPrice: null }],
    rangeMin: 0, rangeMax: 0, promoRangeMin: null, promoRangeMax: null,
    images: [], thumbnailURL: "",
    ingredients: [], allergens: [], preparationTime: "24 hours notice",
    featured: false, available: true, isNew: false, sortOrder: 0,
    discountPercent: null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const updateSize = (idx, key, val) => {
    const newSizes = [...form.sizes];
    newSizes[idx] = { ...newSizes[idx], [key]: key === "price" ? Number(val) : val };
    update("sizes", newSizes);
  };
  const addSize = () => update("sizes", [...form.sizes, { label: "", price: 0, promoPrice: null }]);
  const removeSize = (idx) => update("sizes", form.sizes.filter((_, i) => i !== idx));

  // Image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.id) {
      // For new products, save first then upload
      onSave({ ...form, _pendingImage: file });
      return;
    }
    setUploading(true);
    try {
      const url = await ProductService.uploadImage(form.id, file);
      update("thumbnailURL", url);
      update("images", [...(form.images || []), url]);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="editor-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif" }}>
          {isNew ? "New Product" : `Edit: ${form.name}`}
        </h3>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Product"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        <div className="form-group">
          <label className="form-label">Product Name</label>
          <input className="form-input" value={form.name} onChange={(e) => update("name", e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Category</label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <select className="form-select" style={{ flex: 1 }}
              value={["cakes","cookies","pastries","specialty","breads","drinks","seasonal"].includes(form.category) ? form.category : "__custom__"}
              onChange={(e) => {
                if (e.target.value === "__custom__") {
                  update("category", "");
                } else {
                  update("category", e.target.value);
                }
              }}>
              <option value="cakes">Cakes</option>
              <option value="cookies">Cookies</option>
              <option value="pastries">Pastries</option>
              <option value="specialty">Specialty</option>
              <option value="breads">Breads</option>
              <option value="drinks">Drinks</option>
              <option value="seasonal">Seasonal</option>
              <option value="__custom__">+ Custom Category</option>
            </select>
            {!["cakes","cookies","pastries","specialty","breads","drinks","seasonal"].includes(form.category) && (
              <input className="form-input" style={{ flex: 1 }} value={form.category}
                onChange={(e) => update("category", e.target.value)}
                placeholder="Type category name" />
            )}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-textarea" value={form.description} onChange={(e) => update("description", e.target.value)} />
      </div>

      {/* Image upload */}
      <div className="form-group">
        <label className="form-label">Product Image</label>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          {form.thumbnailURL && (
            <img src={form.thumbnailURL} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover" }} />
          )}
          <button className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()}>
            {uploading ? "Uploading..." : "Upload Image"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
        </div>
      </div>

      {/* PRICING TYPE SELECTOR */}
      <div className="form-group">
        <label className="form-label">Pricing Model</label>
        <div className="pricing-type-selector">
          {["fixed", "per_size", "range"].map((type) => (
            <button key={type}
              className={`pricing-type-btn ${form.pricingType === type ? "active" : ""}`}
              onClick={() => update("pricingType", type)}>
              {type === "fixed" ? "Fixed Price" : type === "per_size" ? "Per Size" : "Price Range"}
            </button>
          ))}
        </div>
      </div>

      {/* Fixed price fields */}
      {form.pricingType === "fixed" && (
        <div style={{ display: "flex", gap: "1rem" }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Price (Rs)</label>
            <input className="form-input" type="number" value={form.fixedPrice || ""}
              onChange={(e) => update("fixedPrice", Number(e.target.value))} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Promo Price (optional)</label>
            <input className="form-input" type="number" value={form.promoPrice || ""}
              onChange={(e) => update("promoPrice", e.target.value ? Number(e.target.value) : null)}
              placeholder="Leave empty if no promo" />
          </div>
        </div>
      )}

      {/* Per size fields */}
      {form.pricingType === "per_size" && (
        <div className="form-group">
          <label className="form-label">Sizes, Prices & Promo Prices</label>
          {form.sizes.map((size, idx) => (
            <div key={idx} className="size-row" style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem" }}>
              <input className="form-input" placeholder='e.g. 6"' value={size.label}
                style={{ flex: "0 0 80px" }}
                onChange={(e) => updateSize(idx, "label", e.target.value)} />
              <input className="form-input" type="number" placeholder="Price" value={size.price}
                style={{ flex: 1 }}
                onChange={(e) => updateSize(idx, "price", e.target.value)} />
              <input className="form-input" type="number" placeholder="Promo (optional)" value={size.promoPrice || ""}
                style={{ flex: 1 }}
                onChange={(e) => updateSize(idx, "promoPrice", e.target.value ? Number(e.target.value) : null)} />
              <button className="btn btn-danger btn-sm" onClick={() => removeSize(idx)}
                disabled={form.sizes.length <= 1}>✕</button>
            </div>
          ))}
          <div style={{ fontSize: "0.75rem", color: "var(--cocoa)", marginBottom: "0.5rem" }}>
            Format: Size | Original Price | Promo Price (leave promo empty if no discount)
          </div>
          <button className="btn btn-outline btn-sm" onClick={addSize} style={{ marginTop: "0.5rem" }}>
            + Add Size
          </button>
        </div>
      )}

      {/* Range fields */}
      {form.pricingType === "range" && (
        <div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Min Price (Rs)</label>
              <input className="form-input" type="number" value={form.rangeMin || ""}
                onChange={(e) => update("rangeMin", Number(e.target.value))} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Max Price (Rs)</label>
              <input className="form-input" type="number" value={form.rangeMax || ""}
                onChange={(e) => update("rangeMax", Number(e.target.value))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "1rem" }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Promo Min Price (optional)</label>
              <input className="form-input" type="number" value={form.promoRangeMin || ""}
                onChange={(e) => update("promoRangeMin", e.target.value ? Number(e.target.value) : null)}
                placeholder="Leave empty if no promo" />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Promo Max Price (optional)</label>
              <input className="form-input" type="number" value={form.promoRangeMax || ""}
                onChange={(e) => update("promoRangeMax", e.target.value ? Number(e.target.value) : null)}
                placeholder="Leave empty if no promo" />
            </div>
          </div>
        </div>
      )}

      {/* Discount percentage — works for all pricing types */}
      <div className="form-group">
        <label className="form-label">Discount % (optional — applies to all pricing types)</label>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <input className="form-input" type="number" min="0" max="100" value={form.discountPercent || ""}
            onChange={(e) => update("discountPercent", e.target.value ? Number(e.target.value) : null)}
            placeholder="e.g. 15" style={{ maxWidth: 120 }} />
          <span style={{ fontSize: "0.82rem", color: "var(--cocoa)" }}>
            {form.discountPercent ? `${form.discountPercent}% off all prices` : "No discount"}
          </span>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Preparation Time</label>
        <input className="form-input" value={form.preparationTime || ""}
          onChange={(e) => update("preparationTime", e.target.value)}
          placeholder="e.g. 24 hours notice" />
      </div>

      <div style={{ display: "flex", gap: "1.5rem", marginTop: "1rem" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={form.featured}
            onChange={(e) => update("featured", e.target.checked)} />
          Featured on Homepage
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={form.available}
            onChange={(e) => update("available", e.target.checked)} />
          Available for Order
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={form.isNew || false}
            onChange={(e) => update("isNew", e.target.checked)} />
          "New" Badge
        </label>
      </div>
    </div>
  );
}

// ============================================================
// MAIN ADMIN PRODUCTS PAGE
// ============================================================
export default function AdminProducts({ showToast }) {
  const { products, loading, refresh } = useProducts(true);
  const [editing, setEditing] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [renamingCat, setRenamingCat] = useState(null);
  const [newCatName, setNewCatName] = useState("");

  // Get unique categories from products
  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const handleRenameCategory = async (oldName) => {
    if (!newCatName.trim() || newCatName === oldName) {
      setRenamingCat(null);
      return;
    }
    try {
      const productsInCat = products.filter((p) => p.category === oldName);
      for (const p of productsInCat) {
        await ProductService.update(p.id, { category: newCatName.trim().toLowerCase() });
      }
      showToast(`Renamed "${oldName}" to "${newCatName}" (${productsInCat.length} products updated)`, "success");
      setRenamingCat(null);
      setNewCatName("");
      refresh();
    } catch (err) {
      showToast("Failed to rename category", "error");
    }
  };

  const handleDeleteCategory = async (catName) => {
    const productsInCat = products.filter((p) => p.category === catName);
    if (productsInCat.length > 0) {
      const moveTo = window.prompt(`"${catName}" has ${productsInCat.length} product(s). Move them to which category?`, "uncategorized");
      if (!moveTo) return;
      try {
        for (const p of productsInCat) {
          await ProductService.update(p.id, { category: moveTo.trim().toLowerCase() });
        }
        showToast(`Moved ${productsInCat.length} products to "${moveTo}" and removed "${catName}"`, "success");
        refresh();
      } catch (err) {
        showToast("Failed to delete category", "error");
      }
    }
  };

  const handleSave = async (formData) => {
    try {
      if (formData.id) {
        // Update existing
        const { id, ...data } = formData;
        await ProductService.update(id, data);
        showToast(`${data.name} updated!`, "success");
      } else {
        // Create new
        const newId = await ProductService.create(formData);
        // Upload pending image if any
        if (formData._pendingImage) {
          await ProductService.uploadImage(newId, formData._pendingImage);
        }
        showToast(`${formData.name} created!`, "success");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      console.error(err);
      showToast("Failed to save product", "error");
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await ProductService.delete(product.id);
      showToast(`${product.name} deleted`, "success");
      refresh();
    } catch (err) {
      showToast("Failed to delete product", "error");
    }
  };

  if (editing !== null) {
    return (
      <ProductEditor
        product={editing.id ? editing : null}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Product Management</h2>
          <p>Add, edit & manage your products with flexible pricing</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn btn-outline btn-sm"
            onClick={() => setShowCategoryManager(!showCategoryManager)}>
            {showCategoryManager ? "Hide Categories" : "Manage Categories"}
          </button>
          <button className="btn btn-primary" onClick={() => setEditing({})}>
            + New Product
          </button>
        </div>
      </div>

      {/* Category Manager */}
      {showCategoryManager && (
        <div className="card" style={{ marginBottom: "1.5rem", padding: "1.25rem" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", marginBottom: "1rem" }}>
            Categories ({categories.length})
          </h3>
          {categories.length === 0 ? (
            <div style={{ fontSize: "0.88rem", color: "var(--cocoa)" }}>No categories yet. Create a product to add one.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {categories.map((cat) => {
                const count = products.filter((p) => p.category === cat).length;
                const isRenaming = renamingCat === cat;
                return (
                  <div key={cat} style={{
                    display: "flex", alignItems: "center", gap: "0.75rem",
                    padding: "0.5rem 0.75rem", borderRadius: 8, background: "var(--cream)",
                  }}>
                    {isRenaming ? (
                      <input className="form-input" style={{ flex: 1, margin: 0 }}
                        value={newCatName}
                        onChange={(e) => setNewCatName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleRenameCategory(cat); if (e.key === "Escape") setRenamingCat(null); }}
                        autoFocus />
                    ) : (
                      <span style={{ flex: 1, fontWeight: 600, textTransform: "capitalize" }}>{cat}</span>
                    )}
                    <span style={{ fontSize: "0.78rem", color: "var(--cocoa)", flexShrink: 0 }}>
                      {count} product{count !== 1 ? "s" : ""}
                    </span>
                    {isRenaming ? (
                      <div style={{ display: "flex", gap: "0.25rem" }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleRenameCategory(cat)}>Save</button>
                        <button className="btn btn-outline btn-sm" onClick={() => setRenamingCat(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: "0.25rem" }}>
                        <button className="btn btn-outline btn-sm" onClick={() => { setRenamingCat(cat); setNewCatName(cat); }}>Rename</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteCategory(cat)}>Delete</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="empty-state"><p>Loading products...</p></div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Pricing</th>
                <th>Price</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    {p.thumbnailURL && (
                      <img src={p.thumbnailURL} alt="" style={{
                        width: 40, height: 40, borderRadius: 6, objectFit: "cover",
                      }} />
                    )}
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      {p.featured && (
                        <span className="inline-tag">★ Featured</span>
                      )}
                    </div>
                  </td>
                  <td style={{ textTransform: "capitalize" }}>{p.category}</td>
                  <td>
                    <span className="inline-tag">{p.pricingType?.replace("_", " ")}</span>
                  </td>
                  <td><PriceDisplay product={p} /></td>
                  <td>⭐ {p.averageRating?.toFixed(1) || "—"} ({p.totalReviews || 0})</td>
                  <td>
                    <span className={`status-badge ${p.available ? "status-confirmed" : "status-cancelled"}`}>
                      {p.available ? "Active" : "Hidden"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setEditing(p)}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}